import { keys } from 'mobx';
import { types, flow, getParent, getRoot } from 'mobx-state-tree';

const URL = 'business-policy/v1/shipping-policies';

const EbayShippingPolicies = types
  .model('EbayShippingPolicies', {
    name: types.optional(types.string, ''),
    handlingTime: types.optional(types.union(types.number, types.string), ''),
    ifUseInternationalShipping: types.optional(types.boolean, false),
    ifUsePoBox: types.optional(types.boolean, true),
    ifEBayGlobalShippingProgram: types.optional(types.boolean, false),
    domesticShippingMethods: types.optional(types.map(types.number), {}),
    internationalShippingMethods: types.optional(types.map(types.number), {}),
    excludedDomesticLocations: types.optional(types.array(types.string), []),
    excludedCountries: types.optional(types.array(types.string), []),
  })
  .views(self => ({
    get sellerAccountCode() {
      return getParent(self).listing.sellerAccountCode;
    },
    get shippingMethods() {
      return getParent(self, 3).shippingMethods || null;
    },
    get policyCode() {
      return getParent(self).policyNormalizedOptions.shipping.code;
    },
    get normalizedDomesticMethods() {
      if (self.domesticShippingMethods) {
        const methodType = 'domesticShippingMethods';
        const methodsToDisplay = self.shippingMethods.domesticList;
        const normalizedDomesticMethods = keys(self.domesticShippingMethods).map((key, index) => ({
          methodId: index,
          methodName: methodsToDisplay[key],
          cost: self.domesticShippingMethods.get(key),
          allMethods: self.getDomesticMethodsToDisplay(key),
          setPrice: price => self.setPrice(methodType, key, price),
          updateMethodType: nextMethodKey => self.updateMethodType(methodType, key, nextMethodKey),
          deleteMethod: () => {
            self.deleteMethod(methodType, key);
          },
        }));
        return normalizedDomesticMethods;
      }
      return [];
    },
    get normalizedInternationalMethods() {
      if (self.internationalShippingMethods) {
        const methodType = 'internationalShippingMethods';
        const methodsToDisplay = self.shippingMethods.internationalList;
        const normalizedDomesticMethods = keys(self.internationalShippingMethods).map(
          (key, index) => ({
            methodId: index,
            methodName: methodsToDisplay[key],
            cost: self.internationalShippingMethods.get(key),
            allMethods: self.getInternationalMethodsToDisplay(key),
            setPrice: price => self.setPrice(methodType, key, price),
            updateMethodType: nextMethodKey =>
              self.updateMethodType(methodType, key, nextMethodKey),
            deleteMethod: () => {
              self.deleteMethod(methodType, key);
            },
          }),
        );
        return normalizedDomesticMethods;
      }
      return [];
    },
    get handlingTimeOptions() {
      return getParent(self).handlingTimeOptions;
    },
    get domesticLocationsList() {
      return getParent(self).domesticLocationsList;
    },
    get countriesList() {
      return getParent(self).countriesList;
    },
    get requestData() {
      const headers = { 'Seller-Account-Id': self.sellerAccountCode };
      const methodsData = {
        domesticShippingMethods: self.domesticShippingMethods,
        internationalShippingMethods: self.internationalShippingMethods,
      };
      return { headers, methodsData };
    },
  }))
  .actions(self => ({
    createPolicy: flow(function* createPolicy(data) {
      data.destinationMarketplaceId = getRoot(self).uiStore.settingsStore.policies.selectedDestId;
      data.currency = getRoot(self).uiStore.settingsStore.userProfile.defaultCurrencyId;
      try {
        const { methodsData } = self.requestData;
        const finalData = {
          ...data,
          ...methodsData,
        };
        const headers = {
          'Seller-Account-Id': getParent(self).sellerAccountCode,
        };
        yield getParent(self, 3).transport5007.post(URL, finalData, {}, headers);
        yield getParent(self, 3).fetchPolicyFormOptions();
      } catch (err) {
        console.log(err);
        yield err;
      }
    }),
    updatePolicy: flow(function* updatePolicy(data) {
      data.destinationMarketplaceId = getRoot(self).uiStore.settingsStore.policies.selectedDestId;
      data.currency = getRoot(self).uiStore.settingsStore.userProfile.defaultCurrencyId;
      try {
        const { methodsData } = self.requestData;
        const finalData = {
          ...methodsData,
          ...data,
          businessPolicyId: self.policyCode,
        };
        const headers = {
          'Seller-Account-Id': getParent(self).sellerAccountCode,
        };
        yield getParent(self, 3).transport5007.update(URL, finalData, {}, headers);
      } catch (err) {
        console.log(err);
      }
    }),
    getDomesticMethodsToDisplay(currentMethodKey) {
      const allMethodsToDisplay = self.shippingMethods.domesticList;
      return Object.keys(allMethodsToDisplay)
        .filter(
          key => self.domesticShippingMethods.get(key) === undefined || key === currentMethodKey,
        )
        .map(key => ({
          methodName: allMethodsToDisplay[key],
          methodKey: key,
        }));
    },
    getInternationalMethodsToDisplay(currentMethodKey) {
      const allMethodsToDisplay = self.shippingMethods.internationalList;
      return Object.keys(allMethodsToDisplay)
        .filter(
          key =>
            self.internationalShippingMethods.get(key) === undefined || key === currentMethodKey,
        )
        .map(key => ({
          methodName: allMethodsToDisplay[key],
          methodKey: key,
        }));
    },
    setPrice(shippingMethodType, method, price) {
      const priceToNumber = parseInt(price, 10);
      self[shippingMethodType].set(method, priceToNumber);
    },
    updateMethodType(methodType, currentMethodKey, nextMethodKey) {
      const currentMethodPrice = self[methodType].get(currentMethodKey);
      const nextShippingMethods = keys(self[methodType]).reduce((acc, method) => {
        const methodKey = method === currentMethodKey ? nextMethodKey : method;
        const cost =
          method === currentMethodKey ? currentMethodPrice : self[methodType].get(method);
        acc.push({
          key: methodKey,
          value: cost,
        });
        return acc;
      }, []);
      self[methodType].clear();
      nextShippingMethods.forEach(m => self[methodType].set(m.key, m.value));
    },
    addDomesticMethod() {
      self.domesticShippingMethods.set('', 0);
    },
    addInternationalMethod() {
      self.internationalShippingMethods.set('', 0);
    },
    checkIfAbleToAddDomesticMethod() {
      if (self.domesticShippingMethods) {
        return (
          keys(self.domesticShippingMethods).length ===
          Object.keys(self.shippingMethods.domesticList).length
        );
      }
      return true;
    },
    checkIfAbleToAddInternationalMethod() {
      if (self.internationalShippingMethods) {
        return (
          keys(self.internationalShippingMethods).length ===
          Object.keys(self.shippingMethods.internationalList).length
        );
      }
      return true;
    },
    deleteMethod(methodType, methodKey) {
      self[methodType].delete(methodKey);
    },
    resetPolicyForm() {
      getParent(self).resetShippingForm();
    },
  }));

export default EbayShippingPolicies;
